import type {
  GameTypePlugin,
  GameRenderer,
  GameConfigurator,
  GameRendererCapabilities,
  CompanionPageMetadata,
} from '../../interfaces/game-type-plugin.interface';
import { PollRenderer } from './poll-renderer';
import type { PollGameSettings } from '@kanek/shared-data-access';
import { SAMPLE_POLL_DEFINITION } from '@kanek/shared-data-access';

/**
 * Poll Game Type Plugin
 *
 * Implements the GameTypePlugin interface for poll/survey/trivia campaigns.
 * Uses the `html-in-3d` rendering mode — a cyberpunk glass tablet with an
 * HTML question overlay projected onto its face.
 *
 * Supports AR mode: tablet lies flat on the AR marker, HTML faces the camera.
 *
 * Reward modes (configured per campaign, not per plugin):
 *   - always:              Participation reward — everyone who answers wins
 *   - on-correct:          Trivia mode — must answer all questions correctly
 *   - on-score-threshold:  Quiz mode — must score >= N% to win
 */
export class PollPlugin implements GameTypePlugin {
  readonly id = 'poll';
  readonly name = 'Poll / Survey / Trivia';
  readonly description =
    'Interactive polling and survey game with a 3D glass tablet and optional trivia scoring';
  readonly version = '1.0.0';

  readonly capabilities: GameRendererCapabilities = {
    mode: 'html-in-3d',          // HTML questions projected onto a 3D glass tablet
    requires3DScene: true,       // Needs 3D box geometry + lighting
    requiresHTMLContainer: true, // Needs HTML container for question/option overlay
    supportsAR: true,            // Fully supports AR marker mode
    supportsVR: false,
  };

  /**
   * Poll Library — data-management companion page for creating and editing
   * poll definitions used by this game type.
   */
  readonly isBeta = true;

  readonly companionPages: CompanionPageMetadata[] = [
    {
      routePath: 'poll-library',
      label: 'Poll Library',
      icon: 'poll',
      requiredPermission: { resource: 'campaigns', action: 'manage' },
    },
  ];

  createRenderer(): GameRenderer {
    return new PollRenderer();
  }

  createConfigurator(): GameConfigurator {
    return new PollConfigurator();
  }
}

/**
 * Poll Game Configurator
 *
 * Provides default settings and schema for the poll game type.
 */
class PollConfigurator implements GameConfigurator {
  getDefaultConfig(): PollGameSettings {
    return {
      type: 'poll',
      pollId: '',                        // Must be set via Poll Library in campaign editor
      rewardMode: 'always',
      scoreThreshold: 70,                // Used only when rewardMode === 'on-score-threshold'
      showResultsAfterSubmit: true,
      tabletStyle: 'cyberpunk',
      questionsPerSession: undefined,    // Show all questions by default
      randomizeQuestionOrder: false,
      randomizeOptionOrder: false,
      accentColor: '#00e5ff',
      tabletWidth: undefined,            // Use default (600px)
      tabletHeight: undefined,           // Use default (800px)
      enableSoundEffects: true,
      // Preview-only: embed sample poll so the studio shows something immediately.
      // Not persisted to Firestore — PollRenderer reads it via (settings as any)._previewPollDefinition.
      ...({ _previewPollDefinition: SAMPLE_POLL_DEFINITION } as any),
    };
  }

  validateConfig(config: unknown): string[] {
    const settings = config as PollGameSettings;
    const errors: string[] = [];

    if (!settings.pollId && !(settings as any)._previewPollDefinition) {
      errors.push('A poll must be selected from the Poll Library.');
    }

    if (
      settings.rewardMode === 'on-score-threshold' &&
      (settings.scoreThreshold === undefined ||
        settings.scoreThreshold < 0 ||
        settings.scoreThreshold > 100)
    ) {
      errors.push('scoreThreshold must be between 0 and 100 when using score-threshold reward mode.');
    }

    return errors;
  }

  getConfigSchema() {
    return {
      sections: [
        {
          title: 'Poll Content',
          description: 'Select a poll definition from the Poll Library',
          fields: [
            {
              key: 'pollId',
              label: 'Poll',
              type: 'text' as const,
              defaultValue: '',
              description: 'Choose a poll from the Poll Library',
            },
          ],
        },
        {
          title: 'Reward Settings',
          fields: [
            {
              key: 'rewardMode',
              label: 'Reward Mode',
              type: 'select' as const,
              defaultValue: 'always',
              description: 'When does the user earn a prize?',
              options: [
                { label: 'Always (participation reward)', value: 'always' },
                { label: 'On Correct Answer (trivia mode)', value: 'on-correct' },
                { label: 'Score Threshold (quiz mode)', value: 'on-score-threshold' },
              ],
            },
            {
              key: 'scoreThreshold',
              label: 'Score Threshold (%)',
              type: 'number' as const,
              defaultValue: 70,
              description: 'Minimum score to win (only used in score-threshold mode)',
              validation: { min: 1, max: 100 },
            },
          ],
        },
        {
          title: 'Display Settings',
          fields: [
            {
              key: 'showResultsAfterSubmit',
              label: 'Show Results After Each Answer',
              type: 'boolean' as const,
              defaultValue: true,
              description: 'Display aggregate bar chart after each question is answered',
            },
            {
              key: 'tabletStyle',
              label: 'Tablet Style',
              type: 'select' as const,
              defaultValue: 'cyberpunk',
              options: [
                { label: 'Cyberpunk (neon cyan)', value: 'cyberpunk' },
                { label: 'Minimal (dark blue)', value: 'minimal' },
                { label: 'Holographic (teal)', value: 'holographic' },
              ],
            },
            {
              key: 'randomizeQuestionOrder',
              label: 'Randomise Question Order',
              type: 'boolean' as const,
              defaultValue: false,
            },
          ],
        },
      ],
    };
  }
}
